import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { REWARD_TYPES, REWARD_TYPE_GROUPS } from '../../model/rewardTypes';
import type { PromotionAction, PromotionActionType } from '../../model/types';
import { ActionCard } from './ActionCard';

function defaultParamsForActionType(type: PromotionActionType): Record<string, unknown> {
  switch (type) {
    case 'PERCENT_OFF':
      return { percent: 10, target: 'ORDER' };
    case 'AMOUNT_OFF':
      return { amount: 0, target: 'ORDER' };
    case 'FIXED_PRICE':
      return { price: 0 };
    case 'SHIPPING_PERCENT_OFF':
      return { percent: 100 };
    default:
      return {};
  }
}

export interface ActionListEditorProps {
  actions: PromotionAction[];
  onChange: (actions: PromotionAction[]) => void;
  hasProductSetCondition: boolean;
  disabled?: boolean;
}

/** "Actions" card — ordered reward-action list + the data-driven reward-type gallery (§8.3-D). */
export function ActionListEditor({
  actions,
  onChange,
  hasProductSetCondition,
  disabled = false,
}: ActionListEditorProps) {
  const { t } = useTranslation('promotions');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const addAction = (type: PromotionActionType) => {
    onChange([...actions, { type, params: defaultParamsForActionType(type) }]);
    setAnchor(null);
  };

  const updateAction = (index: number, next: PromotionAction) => {
    onChange(actions.map((a, i) => (i === index ? next : a)));
  };

  const removeAction = (index: number) => {
    onChange(actions.filter((_, i) => i !== index));
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('builder.actionsCardTitle')}
        </Typography>

        {actions.length === 0 ? (
          <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
            {t('builder.noActionsError')}
          </Typography>
        ) : null}

        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          {actions.map((action, index) => (
            <ActionCard
              // Actions carry no stable id from the server until saved — index is the key.
              key={index}
              action={action}
              index={index}
              hasProductSetCondition={hasProductSetCondition}
              disabled={disabled}
              onChange={(next) => updateAction(index, next)}
              onRemove={() => removeAction(index)}
            />
          ))}
        </Stack>

        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          disabled={disabled}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          {t('builder.addAction')}
        </Button>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          {REWARD_TYPE_GROUPS.flatMap((group) => {
            const entries = REWARD_TYPES.filter((r) => r.group === group);
            if (entries.length === 0) return [];
            return [
              <ListSubheader key={group}>{t(`rewardTypeGroup.${group}`)}</ListSubheader>,
              ...entries.map((entry) => {
                const Icon = entry.icon;
                const item = (
                  <MenuItem
                    key={entry.type}
                    disabled={!entry.available}
                    onClick={entry.available ? () => addAction(entry.type) : undefined}
                  >
                    <ListItemIcon>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t(`rewardType.${entry.type}`)}
                      secondary={t(`rewardTypeDescription.${entry.type}`)}
                    />
                  </MenuItem>
                );
                if (entry.available) return item;
                return (
                  <Tooltip key={entry.type} title={t('builder.comingSoonTooltip')} placement="right">
                    <Box component="span">{item}</Box>
                  </Tooltip>
                );
              }),
            ];
          })}
        </Menu>
      </CardContent>
    </Card>
  );
}
